import React, { useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import spaceImage from '../images/space.webp';
import puzzlePlanetLogo from '../images/logos/title.webp';
import { authApi } from '../services/api';
import { useRecaptcha } from '../hooks/useRecaptcha';

const TrialSignupLink: React.FC = () => {
    const location = useLocation();
    const email = (location.state as { email?: string })?.email || '';
    const [isResending, setIsResending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const { getToken, isLoaded } = useRecaptcha();

    // Redirect if no email in state
    if (!email) {
        return <Navigate to='/trial-signup' replace />;
    }

    const handleResend = async () => {
        setError(null);
        setSuccessMessage(null);
        setIsResending(true);

        if (!isLoaded) {
            setError('Security verification is loading. Please wait a moment and try again.');
            setIsResending(false);
            return;
        }

        try {
            const recaptchaToken = await getToken('email_signup');
            const response = await authApi.startTrialEmailSignup({
                email: email.trim(),
                recaptchaToken,
            });

            if (response.status === 'success') {
                setSuccessMessage('Signup link has been resent to your email.');
            } else {
                setError(response.message || 'Failed to resend signup email. Please try again.');
            }
        } catch (err: any) {
            console.error('Resend email signup error:', err);
            if (err.message && err.message.includes('reCAPTCHA')) {
                setError('Security verification failed. Please try again.');
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
        } finally {
            setIsResending(false);
        }
    };

    return (
        <div
            className='min-h-screen flex flex-col items-center justify-center py-12 px-4 overflow-x-hidden w-full'
            style={{
                backgroundImage: `url(${spaceImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                backgroundRepeat: 'no-repeat',
            }}
        >
            <div className='flex flex-col items-center space-y-8 max-w-2xl w-full'>
                {/* Title Logo */}
                <div className='flex justify-center mb-8'>
                    <img
                        src={puzzlePlanetLogo}
                        alt='Puzzle Planet'
                        className='h-24 md:h-32 w-auto'
                    />
                </div>

                {/* Check Your Inbox Heading */}
                <h1
                    className='text-4xl md:text-5xl font-bold text-center'
                    style={{ color: '#FFB502' }}
                >
                    Check your inbox
                </h1>

                {/* Email Message */}
                <p className='text-xl md:text-2xl text-center' style={{ color: '#FFFFFF' }}>
                    We have sent a signup link to <span className='font-semibold'>{email}</span>.
                    Click the link in the email to finish setting up your account.
                </p>

                {/* Resend Button */}
                <button
                    onClick={handleResend}
                    disabled={isResending || !email}
                    className='px-8 py-4 text-lg font-semibold bg-gradient-to-b from-yellow-400 to-orange-500 text-black rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                    {isResending ? 'Resending...' : 'Resend link'}
                </button>

                {/* Error Message */}
                {error && (
                    <div className='mt-4 text-center text-red-400 text-sm max-w-md'>{error}</div>
                )}

                {/* Success Message */}
                {successMessage && (
                    <div className='mt-4 text-center text-green-400 text-sm max-w-md'>
                        {successMessage}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TrialSignupLink;
